import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase } from "../../lib/supabase";

const DriveAuthContext = createContext(null);

export const DriveAuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async (uid) => {
    if (!uid) {
      setProfile(null);
      return;
    }
    const { data, error } = await supabase
      .from("drive_profiles")
      .select("*")
      .eq("id", uid)
      .maybeSingle();
    if (error) {
      if (error.code === "PGRST205" || (error.message || "").includes("Could not find the table")) {
        console.error("[Drive] Datenbank nicht initialisiert. Bitte DRIVE_MIGRATION.sql im Supabase-SQL-Editor ausführen.");
        setProfile({ id: uid, role: null, _dbMissing: true });
        return;
      }
      console.warn("profile load", error);
      setProfile(null);
      return;
    }
    // Auto-create profile if missing (with role 'artist' by default)
    if (!data) {
      const { data: created, error: insErr } = await supabase
        .from("drive_profiles")
        .insert([{ id: uid, role: "artist" }])
        .select()
        .single();
      if (insErr) {
        console.error(
          "[Drive] Konnte Profil nicht anlegen — vermutlich fehlt die RLS-Policy 'profiles_self_insert'. Bitte DRIVE_HOTFIX_ACCESS.sql im Supabase-SQL-Editor ausführen.",
          insErr
        );
        setProfile(null);
      } else {
        setProfile(created);
      }
    } else {
      setProfile(data);
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      loadProfile(data.session?.user?.id).finally(() => setLoading(false));
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_ev, s) => {
      setSession(s);
      loadProfile(s?.user?.id);
    });
    return () => sub.subscription.unsubscribe();
  }, [loadProfile]);

  const signIn = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signUp = async (email, password) => {
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
    setSession(null);
  };

  const isSuperadmin = profile?.role === "superadmin";
  const isStaff = profile?.role === "staff" || isSuperadmin;
  const role = profile?.role || null;

  return (
    <DriveAuthContext.Provider
      value={{ session, profile, loading, signIn, signUp, signOut, isSuperadmin, isStaff, role, reload: () => loadProfile(session?.user?.id) }}
    >
      {children}
    </DriveAuthContext.Provider>
  );
};

export const useDriveAuth = () => useContext(DriveAuthContext);
